<script>
import * as d3 from 'd3'
import { camelCase, keyBy, mapKeys, mapValues } from 'lodash'
import chart from '@/mixins/chart'

export default {
  mixins: [chart],
  name: 'ChartCompaniesByCountry',
  filters: {
    formatNumber: d3.format(',')
  },
  props: {
    valueField: {
      type: String,
      default: 'companyCount'
    }
  },
  computed: {
    tsv () {
      return this.$options.tsv.map(row => {
        return mapKeys(row, (_, k) => camelCase(k))
      })
    },
    tsvHashByIso3 () {
      return keyBy(this.tsv, 'iso3')
    },
    valuesByIso3 () {
      return mapValues(this.tsvHashByIso3, this.valueField)
    }
  }
}
</script>

<template>
  <div class="chart-bo-companies px-4 pt-4 pb-0 world-map">
    <h4>{{ chartRoute.title }}</h4>
    <p>There are more than 27,000 companies in the data. Hover to see the number of companies tied to beneficial owners in each country.</p>
    <choropleth-map
      :data="valuesByIso3"
      :min="0"
      :social-mode="socialMode"
      clickable
      class="chart-bo-companies__map">
      <template #legend-cursor="{ identifier, value }">
        <div class="bg-secondary font-weight-normal p-2 text-light text-center" v-if="identifier in tsvHashByIso3">
          <span class="font-weight-bold">
            {{ value | formatNumber }}
          </span>&nbsp;companies tied to owners
          <span class="font-weight-bold">
            in {{ tsvHashByIso3[identifier].countryName.split(', ').reverse().join(' ') }}
          </span>
        </div>
      </template>
    </choropleth-map>
    <p class="text-muted mt-2 small text-right">Source: Pandora Papers data</p>
    <p class="mt-2 font-italic">A company can have beneficial owners from more than one place.</p>
  </div>
</template>

<style lang="scss" scoped>
@import '../variables.scss';

.chart-bo-companies {
  overflow: hidden;
  min-height: 500px;
  height: 100%;
  width: 100%;
  background: url('../assets/images/bg-paper.jpg') $light no-repeat center center;
  background-size: cover;
  display: flex;
  flex-direction: column;

  .chart--social-mode & {
    background-image: url('../assets/images/bg-dark-paper.jpg');
    background-color: $secondary;
  }

  .chart--active-route-view & {
    height: calc(100vh - var(--app-footer-height, 0));
  }

  & /deep/ &__map {
    flex-grow: 1;
  }

  & /deep/ &__map .scale-legend {
    top: 100%;
    bottom: auto;

    &__cursor {
      width: 150px;
    }
  }
}
</style>

<tsv>
iso3	country_name	company_count
GBR	United Kingdom	3936.00
RUS	Russian Federation	3694.00
HKG	Hong Kong	2104.00
CHN	China	1892.00
ARG	Argentina	1448.00
BRA	Brazil	1407.00
UKR	Ukraine	1373.00
CHE	Switzerland	1086.00
ITA	Italy	1062.00
CYP	Cyprus	994.00
MCO	Monaco	960.00
ARE	United Arab Emirates	947.00
TWN	Taiwan	946.00
VEN	Venezuela	863.00
FRA	France	794.00
ESP	Spain	751.00
USA	United States	701.00
DEU	Germany	566.00
ISR	Israel	531.00
PAN	Panama	524.00
CAN	Canada	493.00
PRT	Portugal	487.00
COL	Colombia	444.00
IND	India	420.00
URY	Uruguay	418.00
ZAF	South Africa	412.00
LBN	Lebanon	412.00
GTM	Guatemala	396.00
NLD	Netherlands	323.00
KAZ	Kazakhstan	314.00
SGP	Singapore	301.00
MEX	Mexico	297.00
GRC	Greece	283.00
AUS	Australia	263.00
KOR	Korea	253.00
CHL	Chile	248.00
DOM	Dominican Republic	248.00
NGA	Nigeria	233.00
PHL	Philippines	224.00
TUR	Turkey	221.00
POL	Poland	207.00
BEL	Belgium	192.00
SAU	Saudi Arabia	187.00
PER	Peru	180.00
EGY	Egypt	180.00
CZE	Czech Republic	176.00
THA	Thailand	175.00
BGR	Bulgaria	174.00
IDN	Indonesia	171.00
JOR	Jordan	170.00
AUT	Austria	164.00
ROU	Romania	163.00
IRL	Ireland	158.00
VGB	Virgin Islands, British	147.00
CRI	Costa Rica	147.00
HND	Honduras	146.00
SLV	El Salvador	145.00
SWE	Sweden	144.00
BLR	Belarus	138.00
KEN	Kenya	134.00
JPN	Japan	134.00
SRB	Serbia	123.00
MYS	Malaysia	118.00
MAR	Morocco	115.00
LUX	Luxembourg	114.00
LVA	Latvia	113.00
BHS	Bahamas	111.00
ECU	Ecuador	109.00
MLT	Malta	103.00
GEO	Georgia	95.00
PAK	Pakistan	95.00
QAT	Qatar	93.00
LTU	Lithuania	93.00
AGO	Angola	92.00
VNM	Vietnam	89.00
NOR	Norway	88.00
SYC	Seychelles	79.00
HUN	Hungary	77.00
NZL	New Zealand	77.00
IRQ	Iraq	77.00
MDA	Moldova, Republic of	76.00
AZE	Azerbaijan	76.00
LIE	Liechtenstein	76.00
JEY	Jersey	74.00
SYR	Syrian Arab Republic	74.00
UZB	Uzbekistan	70.00
KNA	Saint Kitts and Nevis	69.00
ARM	Armenia	67.00
DNK	Denmark	66.00
IMN	Isle of Man	66.00
KWT	Kuwait	62.00
NIC	Nicaragua	61.00
EST	Estonia	61.00
GGY	Guernsey	58.00
FIN	Finland	55.00
HRV	Croatia	51.00
TJK	Tajikistan	50.00
ZWE	Zimbabwe	49.00
SVK	Slovakia	47.00
BLZ	Belize	46.00
IRN	Iran, Islamic Republic of	46.00
CUB	Cuba	40.00
SVN	Slovenia	40.00
BHR	Bahrain	39.00
GHA	Ghana	39.00
MUS	Mauritius	39.00
CYM	Cayman Islands	36.00
GIB	Gibraltar	34.00
DZA	Algeria	32.00
PRY	Paraguay	31.00
CIV	Côte d'Ivoire	31.00
MAC	Macao	30.00
BOL	Bolivia	29.00
AND	Andorra	28.00
KGZ	Kyrgyzstan	27.00
SDN	Sudan	27.00
TUN	Tunisia	26.00
CUW	Curaçao	24.00
LBY	Libya	24.00
TZA	Tanzania	23.00
LKA	Sri Lanka	22.00
TKM	Turkmenistan	22.00
OMN	Oman	21.00
ATG	Antigua and Barbuda	21.00
MOZ	Mozambique	20.00
ZMB	Zambia	20.00
SEN	Senegal	20.00
HTI	Haiti	17.00
COD	Congo, The Democratic Republic of the	15.00
DMA	Dominica	15.00
PSE	Palestine, State of	15.00
YEM	Yemen	15.00
BRB	Barbados	15.00
COG	Congo	13.00
BMU	Bermuda	13.00
REU	Réunion	13.00
ALB	Albania	13.00
MNE	Montenegro	13.00
MDG	Madagascar	12.00
GAB	Gabon	11.00
MHL	Marshall Islands	10.00
LAO	Lao People's Democratic Republic	10.00
BGD	Bangladesh	10.00
TTO	Trinidad and Tobago	9.00
AIA	Anguilla	8.00
UGA	Uganda	8.00
JAM	Jamaica	8.00
MLI	Mali	8.00
CMR	Cameroon	8.00
MWI	Malawi	8.00
ISL	Iceland	8.00
KHM	Cambodia	8.00
IOT	British Indian Ocean Territory	8.00
TCD	Chad	7.00
BVT	Bouvet Island	7.00
BIH	Bosnia and Herzegovina	6.00
SXM	Sint Maarten (Dutch part)	6.00
XKX	Kosovo	6.00
GIN	Guinea	5.00
GRD	Grenada	5.00
ETH	Ethiopia	5.00
LBR	Liberia	5.00
NER	Niger	5.00
MNG	Mongolia	5.00
PRI	Puerto Rico	5.00
LCA	Saint Lucia	5.00
MRT	Mauritania	4.00
SLE	Sierra Leone	4.00
TCA	Turks and Caicos Islands	4.00
PNG	Papua New Guinea	4.00
NPL	Nepal	4.00
BRN	Brunei Darussalam	3.00
VUT	Vanuatu	3.00
RWA	Rwanda	3.00
ABW	Aruba	3.00
BFA	Burkina Faso	3.00
FJI	Fiji	3.00
COK	Cook Islands	3.00
BEN	Benin	3.00
SOM	Somalia	2.00
AFG	Afghanistan	2.00
NAM	Namibia	2.00
SWZ	Eswatini	2.00
MMR	Myanmar	2.00
MKD	North Macedonia	2.00
WSM	Samoa	2.00
DJI	Djibouti	2.00
STP	Sao Tome and Principe	1.00
TGO	Togo	1.00
SGS	South Georgia and the South Sandwich Islands	1.00
BWA	Botswana	1.00
UMI	United States Minor Outlying Islands	1.00
ERI	Eritrea	1.00
COM	Comoros	1.00
GUY	Guyana	1.00
BLM	Saint Barthélemy	1.00
#N/A	Korea, Republic of	1.00
SUR	Suriname	1.00
GNB	Guinea-Bissau	1.00
PYF	French Polynesia	1.00
BES	Bonaire, Sint Eustatius and Saba	1.00
VIR	Virgin Islands, U.S.	1.00
NCL	New Caledonia	1.00
BDI	Burundi	1.00
GNQ	Equatorial Guinea	1.00
MDV	Maldives	1.00
</tsv>
